// client/src/hooks/useMenu.js
import { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../utils/apiClient';
import { useTenant } from '../contexts/TenantContext';
import { useDocumentCache } from './useDocumentCache';
import { getBackendBaseOrigin } from '../utils/network';

/**
 * Hook ottimizzato per caricare i documenti con cache
 */
export default function useMenu(tenantIdOverride = null) {
    const { tenantId: ctxTenantId } = useTenant();
    const tenantId = tenantIdOverride || ctxTenantId;
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { getDocument, clearCache, prefetchDocument } = useDocumentCache();

    const load = useCallback(async (type = null) => {
        if (!tenantId) {
            setDocuments([]);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams();
            if (type) params.set('type', String(type));
            params.set('ts', Date.now().toString());
            const query = params.toString();
            const url = `/api/public/tenants/${tenantId}/documents${query ? `?${query}` : ''}`;
            const res = await apiFetch(url, {
                tenantId: String(tenantId),
                cache: 'no-store',
                headers: {
                    'Cache-Control': 'no-cache',
                    'Pragma': 'no-cache'
                }
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            setDocuments(Array.isArray(data) ? data : []);
        } catch (err) {
            setError(err?.message || 'load_error');
            setDocuments([]);
        } finally {
            setLoading(false);
        }
    }, [tenantId]);

    useEffect(() => {
        if (tenantId) {
            load();
            // Prefetch dei documenti più comuni
            ['MENU', 'ALLERGENI', 'EVENTO', 'MENU_FESTE'].forEach(type => {
                prefetchDocument(tenantId, type);
            });
        }
    }, [tenantId, load, prefetchDocument]);

    const getDocumentsByType = useCallback((type) =>
        documents.filter(d => String(d.docType || d.type).toLowerCase() === String(type).toLowerCase()),
        [documents]
    );

    const getDocumentUrl = useCallback((type) => {
        const docs = documents.filter(doc => String(doc.docType || doc.type).toLowerCase() === String(type).toLowerCase());
        if (docs.length === 0) return null;

        // Se ci sono più documenti dello stesso tipo, prendi il più recente
        const latestDoc = docs.sort((a, b) => {
            // Prima prova con updatedAt
            if (a.updatedAt && b.updatedAt) {
                return new Date(b.updatedAt) - new Date(a.updatedAt);
            }
            // Fallback su ID (più alto = più recente)
            return (b.id || 0) - (a.id || 0);
        })[0];

        const url = latestDoc?.url || null;

        if (!url) return null;

        // Se l'URL inizia con /, aggiungi il backend URL dinamico
        if (url.startsWith('/')) {
            return `${getBackendBaseOrigin()}${url}`;
        }
        return url;
    }, [documents]);

    const refreshCache = useCallback(() => {
        clearCache(tenantId);
        load();
    }, [tenantId, clearCache, load]);

    return {
        documents,
        loading,
        error,
        load,
        getDocumentsByType,
        getDocumentUrl,
        refreshCache,
        reload: load
    };
}